import PropertyPanel from '../widgets/PropertyPanel.js';

/**
 * TextPropertyPanel - UI for text material properties
 */
export default class TextPropertyPanel extends PropertyPanel {
  render(initialData = {}) {
    this.clear();

    // Text content
    const textInput = this.builder.createInput(initialData.text || 'Sample Text', 'text');
    textInput.className = 'gui-input gui-input-full';
    textInput.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    this.addRow('Text', textInput, true);
    this.controls.textInput = textInput;

    // Font size
    const fontSizeSlider = this.addSlider('Font Size', 8, 72, 1, initialData.fontSize || 16, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.fontSizeSlider = fontSizeSlider;

    // Text color
    const textColor = this.addColorPicker('Text Color', initialData.textColor || '#ffffff');
    textColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.textColor = textColor;

    // Background color
    const bgColor = this.addColorPicker('Background', initialData.bgColor || '#3498db');
    bgColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.bgColor = bgColor;

    // Font weight
    const weightSelect = this.addSelect('Weight', [
      { value: 'normal', label: 'Normal' },
      { value: 'bold', label: 'Bold' }
    ], initialData.fontWeight || 'normal');
    weightSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.weightSelect = weightSelect;

    // Text align
    const alignSelect = this.addSelect('Align', [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' }
    ], initialData.textAlign || 'center');
    alignSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.alignSelect = alignSelect;

    // Text axis selector for triplanar projection
    const axisSelect = this.addSelect('Project On Axes', [
      { value: 'xz', label: 'XZ (Floor/Ceiling - Default)' },
      { value: 'xy', label: 'XY (Front/Back walls)' },
      { value: 'yz', label: 'YZ (Left/Right walls)' },
      { value: 'xyz', label: 'All Axes' },
      { value: 'x', label: 'X only' },
      { value: 'y', label: 'Y only' },
      { value: 'z', label: 'Z only' }
    ], initialData.axis || 'xz');
    axisSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.axisSelect = axisSelect;

    // XYZ Scale controls
    const scaleSection = this.builder.createAnimatedSubsection('Scale', 1, false);
    const xyzControl = this.builder.createXYZControl(
      (axis, value, isLinked) => {
        if (this.onChange) this.onChange();
      },
      (linked) => {
        if (this.onChange) this.onChange();
      }
    );
    scaleSection.body.appendChild(xyzControl.control);
    this.container.appendChild(scaleSection.subsection);
    this.controls.scaleControl = xyzControl;

    // Offset X control
    const offsetXSlider = this.addSlider('Offset X', -500, 500, 1, initialData.offsetX || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetXSlider = offsetXSlider;

    // Offset Y control
    const offsetYSlider = this.addSlider('Offset Y', -500, 500, 1, initialData.offsetY || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetYSlider = offsetYSlider;

    // Offset Z control
    const offsetZSlider = this.addSlider('Offset Z', -500, 500, 1, initialData.offsetZ || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetZSlider = offsetZSlider;
  }

  getData() {
    return {
      type: 'text',
      text: this.controls.textInput.value,
      fontSize: parseFloat(this.controls.fontSizeSlider.slider.value),
      textColor: this.controls.textColor.value,
      bgColor: this.controls.bgColor.value,
      fontWeight: this.controls.weightSelect.value,
      textAlign: this.controls.alignSelect.value,
      axis: this.controls.axisSelect.value,
      scaleX: parseFloat(this.controls.scaleControl.controls.x.slider.value),
      scaleY: parseFloat(this.controls.scaleControl.controls.y.slider.value),
      scaleZ: parseFloat(this.controls.scaleControl.controls.z.slider.value),
      offsetX: parseFloat(this.controls.offsetXSlider.slider.value),
      offsetY: parseFloat(this.controls.offsetYSlider.slider.value),
      offsetZ: parseFloat(this.controls.offsetZSlider.slider.value)
    };
  }

  setData(data) {
    if (data.text) this.controls.textInput.value = data.text;
    if (data.fontSize !== undefined) {
      this.controls.fontSizeSlider.slider.value = data.fontSize;
      this.controls.fontSizeSlider.valueInput.value = data.fontSize;
    }
    if (data.textColor) this.controls.textColor.value = data.textColor;
    if (data.bgColor) this.controls.bgColor.value = data.bgColor;
    if (data.fontWeight) this.controls.weightSelect.value = data.fontWeight;
    if (data.textAlign) this.controls.alignSelect.value = data.textAlign;
    if (data.axis) this.controls.axisSelect.value = data.axis;
    if (data.scaleX !== undefined) {
      this.controls.scaleControl.controls.x.slider.value = data.scaleX;
      this.controls.scaleControl.controls.x.valueInput.value = data.scaleX;
    }
    if (data.scaleY !== undefined) {
      this.controls.scaleControl.controls.y.slider.value = data.scaleY;
      this.controls.scaleControl.controls.y.valueInput.value = data.scaleY;
    }
    if (data.scaleZ !== undefined) {
      this.controls.scaleControl.controls.z.slider.value = data.scaleZ;
      this.controls.scaleControl.controls.z.valueInput.value = data.scaleZ;
    }
    if (data.offsetX !== undefined) {
      this.controls.offsetXSlider.slider.value = data.offsetX;
      this.controls.offsetXSlider.valueInput.value = data.offsetX;
    }
    if (data.offsetY !== undefined) {
      this.controls.offsetYSlider.slider.value = data.offsetY;
      this.controls.offsetYSlider.valueInput.value = data.offsetY;
    }
    if (data.offsetZ !== undefined) {
      this.controls.offsetZSlider.slider.value = data.offsetZ;
      this.controls.offsetZSlider.valueInput.value = data.offsetZ;
    }
  }
}
